import React, { useState } from 'react';
import { LogInIcon, AlertCircleIcon, GaugeIcon, ShieldIcon } from 'lucide-react';
interface LoginScreenProps {
  onLogin: (username: string, password: string) => boolean;
}
export function LoginScreen({
  onLogin
}: LoginScreenProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    setTimeout(() => {
      const success = onLogin(username, password);
      if (!success) {
        setError("Nom d'utilisateur ou mot de passe incorrect");
        setPassword('');
      }
      setIsLoading(false);
    }, 500);
  };
  return <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4" data-id="element-66">
      <div className="w-full max-w-md" data-id="element-67">
        {/* Logo & Title */}
        <div className="text-center mb-8" data-id="element-68">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg mb-4" data-id="element-69">
            <GaugeIcon className="w-10 h-10 text-white" data-id="element-70" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-71">
            Gestion de File d'Attente
          </h1>
          <p className="text-gray-600" data-id="element-72">Stations Service - Bamako</p>
        </div>
        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8" data-id="element-73">
          <div className="flex items-center gap-3 mb-6" data-id="element-74">
            <ShieldIcon className="w-6 h-6 text-blue-600" data-id="element-75" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-76">Connexion</h2>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-77">
            {error && <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3" data-id="element-78">
                <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-79" />
                <p className="text-sm text-red-800 font-medium" data-id="element-80">{error}</p>
              </div>}
            <div data-id="element-81">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-82">
                Nom d'utilisateur
              </label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors" placeholder="Entrez votre nom d'utilisateur" required disabled={isLoading} data-id="element-83" />
            </div>
            <div data-id="element-84">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-85">
                Mot de passe
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors" placeholder="Entrez votre mot de passe" required disabled={isLoading} data-id="element-86" />
            </div>
            <button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-87">
              {isLoading ? <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-88" />
                  Connexion en cours...
                </> : <>
                  <LogInIcon className="w-5 h-5" data-id="element-89" />
                  Se connecter
                </>}
            </button>
          </form>
          {/* Demo Credentials */}
          <div className="mt-6 pt-6 border-t-2 border-gray-100" data-id="element-90">
            <p className="text-xs text-gray-500 font-semibold mb-3" data-id="element-91">
              Comptes de démonstration:
            </p>
            <div className="space-y-2 text-xs" data-id="element-92">
              <div className="bg-blue-50 rounded-lg p-3" data-id="element-93">
                <p className="font-semibold text-blue-900 mb-1" data-id="element-94">
                  👑 Administrateur
                </p>
                <p className="text-blue-700" data-id="element-95">admin / admin123</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3" data-id="element-96">
                <p className="font-semibold text-orange-900 mb-1" data-id="element-97">
                  👁️ Superviseur Général
                </p>
                <p className="text-orange-700" data-id="element-98">generalsuper / gensuper123</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3" data-id="element-99">
                <p className="font-semibold text-purple-900 mb-1" data-id="element-100">
                  👨‍💼 Superviseur
                </p>
                <p className="text-purple-700" data-id="element-101">supervisor1 / super123</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3" data-id="element-102">
                <p className="font-semibold text-green-900 mb-1" data-id="element-103">
                  👷 Opérateur
                </p>
                <p className="text-green-700" data-id="element-104">operator1 / oper123</p>
              </div>
            </div>
          </div>
        </div>
        {/* CECOGEC Footer */}
        <div className="mt-6 text-center" data-id="element-105">
          <p className="text-gray-600 font-semibold text-lg" data-id="element-106">CECOGEC</p>
          <p className="text-gray-500 text-sm mt-1" data-id="element-107">
            Centre de Coordination et de Gestion des Crises et Catastrophes
          </p>
        </div>
      </div>
    </div>;
}